import { Directive, ElementRef, HostListener, Input } from '@angular/core';

/**
 * Decimal comma fix for iOS/Android/Desktop:
 * - User can type "," as decimal separator (FR habits).
 * - We normalize to "." inside the input so the app sends "1.2" to APIs.
 *
 * Works with:
 * - <input type="text" inputmode="decimal"> (recommended: stable caret on iOS)
 * - <input type="number"> (legacy, but caret can be weird on iOS)
 */
@Directive({
  selector: '[appDecimalCommaFix]',
  standalone: true,
})
export class DecimalCommaFixDirective {
  @Input() commaFixLog?: (m: string) => void;

  constructor(private el: ElementRef<HTMLInputElement>) { }

  private get input(): HTMLInputElement {
    return this.el.nativeElement;
  }

  private log(message: string, extra?: any) {
    try {
      if (!this.commaFixLog) return;
      const msg = extra ? `${message} | ${JSON.stringify(extra)}` : message;
      this.commaFixLog(msg);
    } catch {
      // noop
    }
  }

  private getSelStart(): number {
    const i = this.input;
    return typeof i.selectionStart === 'number' ? i.selectionStart : i.value.length;
  }

  private getSelEnd(): number {
    const i = this.input;
    return typeof i.selectionEnd === 'number' ? i.selectionEnd : i.value.length;
  }

  private applyCaret(pos: number) {
    const i = this.input;
    try { i.setSelectionRange(pos, pos); } catch { /* noop */ }
  }

  private forceCaret(pos: number) {
    const safePos = Math.max(0, pos);
    this.applyCaret(safePos);

    try {
      requestAnimationFrame(() => {
        this.applyCaret(safePos);
        requestAnimationFrame(() => this.applyCaret(safePos));
      });
    } catch {
      // noop
    }

    setTimeout(() => this.applyCaret(safePos), 0);
  }

  private injectText(text: string): number {
    const i = this.input;
    const start = this.getSelStart();
    const end = this.getSelEnd();

    try {
      i.setRangeText(text, start, end, 'end');
    } catch {
      const before = i.value.slice(0, start);
      const after = i.value.slice(end);
      i.value = before + text + after;
      this.applyCaret(start + text.length);
    }

    i.dispatchEvent(new Event('input', { bubbles: true }));
    return start + text.length;
  }

  @HostListener('beforeinput', ['$event'])
  onBeforeInput(ev: InputEvent) {
    // Only normal typing
    if (ev.inputType !== 'insertText') return;

    const data = (ev as any).data as string | null | undefined;
    if (data !== ',') return;

    ev.preventDefault();

    // Insert "." instead of ","
    const caretAfter = this.injectText('.');
    this.forceCaret(caretAfter);

    this.log(`[amount][comma] beforeinput ',' -> '.'`, {
      after: this.input.value,
      caretAfter,
    });
  }

  @HostListener('paste', ['$event'])
  onPaste(ev: ClipboardEvent) {
    const raw = ev.clipboardData?.getData('text') ?? '';
    if (!raw) return;

    const fixed = raw.replace(/,/g, '.');
    if (fixed === raw) return;

    ev.preventDefault();

    const caretAfter = this.injectText(fixed);
    this.forceCaret(caretAfter);

    this.log(`[amount][comma] paste normalize`, { raw, fixed, caretAfter });
  }
}
